import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from './client';
import { useAuthStore } from '../stores/auth.store';

export interface ManageAsTarget {
  org_id: string;
  account_id: string;
  user_id: string;
  role: string;
  name: string;
  email: string;
}

export interface ManageAsSession {
  token: string;
  expires_at: string;
  actor_email: string;
  target: ManageAsTarget;
}

/**
 * Start a "Manage as" session for a (non-admin) member of the caller's org.
 * The caller opens the returned token in a new tab via openImpersonationTab.
 *
 * Errors carry `{ error, code }`: TARGET_IS_ADMIN (403), TARGET_NO_ENGAGE_ACCESS
 * (409), ENGAGE_ROLE_REQUIRED / NOT_ALLOWED_WHILE_IMPERSONATING (403), or 404.
 */
export async function startManageAs(targetUserId: string): Promise<ManageAsSession> {
  const { data } = await apiClient.post<{ data: ManageAsSession }>('/manage-as', {
    target_user_id: targetUserId,
  });
  return data.data;
}

export function useStartManageAs() {
  return useMutation({ mutationFn: startManageAs });
}

/**
 * End the current impersonation session (Manage as, or a takeover token that
 * carries a jti). The token stops working immediately.
 *
 * Deliberately bypasses apiClient: if the token has already expired or been
 * revoked, the 401 must not trigger the global logout redirect — callers
 * ignore failures and close the tab regardless.
 */
export async function endManageAs(): Promise<void> {
  const token = useAuthStore.getState().token;
  if (!token) return;
  await axios.post('/api/v1/manage-as/end', null, {
    headers: { Authorization: `Bearer ${token}` },
  });
}
